import { defineStore } from 'pinia'
import { computed, h, reactive, ref, render, watch } from 'vue'
import { useRoute } from 'vue-router'

import { c11, getEntries, waitForElement } from '@/lib/utils'
import { usePDF } from './pdfStore'

import type { FieldObject, FieldObjects } from '@/types'

interface Signature {
  data_uri: string
  annotation_id: string
  annotation_field_name: string
}

export const useSignatures = defineStore('signatures', () => {
  const route = useRoute()
  const pdf = usePDF()

  const showSignModal = ref<boolean>(false)
  const signatureFields = ref<FieldObjects | null>(null)
  const selectedSignatureFieldObject = ref<FieldObject | null>(null)

  const signatureRegexp =
    route.name === 'PDF Documents' || route.name === 'External PDF Documents'
      ? /^(?!.*lawfirm).*signature\d*/i
      : /signature\d*_lawfirm|signature_lawfirm\d*/i

  const signatures = reactive<{ [key: string]: Signature }>({})
  const cancelFunctions = new Map<string, () => void>()

  const hasAllSignatures = computed(() => {
    if (!signatureFields.value) return true
    return Object.values(signatures).length === Object.values(signatureFields.value).length
  })

  async function findAllSignatureFields(fieldObjects: FieldObjects): Promise<void> {
    if (!pdf.selectedForm) return
    signatureFields.value = Object.fromEntries(
      getEntries(fieldObjects).filter(([key]) => key.search(signatureRegexp) !== -1)
    )
  }

  function checkSignatures(): boolean {
    if (!signatureFields.value) return true
    const signatureNames = Object.values(signatures).map((x) => x.annotation_field_name)
    return Object.values(signatureFields.value)
      .flat()
      .every((elem) => signatureNames.includes(elem.name))
  }

  function updateSignatureAnnotation(annotation: FieldObject, el: HTMLElement): void {
    if (!pdf.selectedForm) {
      console.warn(
        [
          '%cupdateSignatureAnnotation: selectedForm is',
          `${pdf.selectedForm}`,
          'This should never happen.'
        ].join(' '),
        c11
      )
      return
    }

    const signatureKey = pdf.selectedForm.documentProperties.id + annotation.name
    const signature = signatures[signatureKey]
    if (signature) {
      render(
        h('img', {
          src: signature.data_uri,
          alt: annotation.name,
          class: 'signature',
          style: {
            border: '2px solid #c44747',
            cursor: 'crosshair',
            height: '60px',
            position: 'relative',
            top: '-30px',
            zIndex: -65536
          },
          onClick: () => {
            selectedSignatureFieldObject.value = annotation
            showSignModal.value = !showSignModal.value
          }
        }),
        el
      )
    } else {
      render(
        h(
          'div',
          {
            style: {
              alignItems: 'center',
              backgroundColor: '#c4474777',
              border: '2px dashed #c44747',
              borderRadius: '2px',
              color: '#c44747',
              cursor: 'crosshair',
              objectFit: 'contain',
              overflow: 'hidden',
              padding: '4px',
              zIndex: 65536
            },
            onClick: () => {
              selectedSignatureFieldObject.value = annotation
              showSignModal.value = !showSignModal.value
            },
            'v-resize-text': {
              ratio: 1.3,
              minFontSize: '30px',
              maxFontSize: '50px',
              delay: 200
            }
          },
          'Unterschrift'
        ),
        el
      )
    }
  }

  async function updateAll(fieldObjects: FieldObjects): Promise<void> {
    const signatureEntries = getEntries(fieldObjects).filter(([key]) => signatureRegexp.test(key))

    signatureEntries.forEach(([, fieldObjects]) => {
      fieldObjects.forEach((fieldObject) => {
        markSignature(fieldObject)
      })
    })
  }

  async function markSignature(fieldObject: FieldObject | null): Promise<void> {
    if (!fieldObject) return
    const name = `section[data-annotation-id="${fieldObject.id}"]`

    // Cancel any existing operation for this field object
    if (cancelFunctions.has(fieldObject.id)) {
      cancelFunctions.get(fieldObject.id)!()
      cancelFunctions.delete(fieldObject.id)
    }

    const { promise, cancel } = waitForElement(name)
    cancelFunctions.set(fieldObject.id, cancel)

    promise.then((el: Element) => {
      updateSignatureAnnotation(fieldObject, el as HTMLElement)
      cancelFunctions.delete(fieldObject.id)
    })
  }

  watch(signatures, () => {
    markSignature(selectedSignatureFieldObject.value)
    hasAllSignatures.value = checkSignatures()
  })

  watch(selectedSignatureFieldObject, () => {
    markSignature(selectedSignatureFieldObject.value)
  })

  function clearSignatures(): void {
    signatureFields.value = null
    selectedSignatureFieldObject.value = null
    showSignModal.value = false
    Object.keys(signatures).forEach((key) => {
      delete signatures[key]
    })
  }

  // New cleanup function
  function cleanup() {
    cancelFunctions.forEach((cancel) => cancel())
    cancelFunctions.clear()
  }

  return {
    checkSignatures,
    findAllSignatureFields,
    hasAllSignatures,
    selectedSignatureFieldObject,
    showSignModal,
    signatureFields,
    signatures,
    updateAll,
    clearSignatures,
    cleanup
  }
})
